// @flow

import * as React from 'react';

import type { NotifyAction } from '@gandi/notifications';
import type { TranslatorProps } from '@gandi/translate';
import { Cell, Base } from '@gandi/ui';
import { ListRow, ListItem, ListIcon, TitleCell } from '@gandi/ui/deprecated';

import type {
  ExtendedRenewableProduct,
  QuotedProduct,
} from 'src/DomainRenew/componentsNDS/types';

import RenewableProductRow from './RenewableProductRow';

export type Props = {
  quotedProducts: Array<QuotedProduct>,
  entries: Array<ExtendedRenewableProduct>,
  translator: TranslatorProps,
  notifyDanger: NotifyAction,
  addRenewableProductsToCart: (
    translator: TranslatorProps,
    products: Array<ExtendedRenewableProduct>,
  ) => Promise<any>,
  productType: string,
  loading: boolean,
  loadingQuotations: boolean,
  ...
};

type State = {
  loading: boolean,
  ...
};

export default class SubList extends React.PureComponent<Props, State> {
  state: State = {
    loading: false,
  };

  handleAddAllToCartClick: (evt: SyntheticMouseEvent<>) => Promise<void> =
    async (evt: SyntheticMouseEvent<>) => {
      evt.preventDefault();

      if (this.state.loading) {
        return;
      }

      const {
        entries,
        translator: { __ },
      } = this.props;

      this.setState({ loading: true });

      try {
        await this.props.addRenewableProductsToCart(
          this.props.translator,
          entries,
        );
      } catch (err) {
        this.props.notifyDanger(__('Unable to add the products in the cart.'));
      }

      this.setState({ loading: false });
    };

  renderRenewableProductRow: (entry: ExtendedRenewableProduct) => React.Node = (
    entry: ExtendedRenewableProduct,
  ) => (
    <Base tagName="li" backgroundColor="white" key={entry.id} separatorBottom>
      <RenewableProductRow
        entry={entry}
        quotedProduct={this.props.quotedProducts.find(
          quotedProduct => quotedProduct.name === entry.name,
        )}
        loadingQuotations={this.props.loadingQuotations}
        translator={this.props.translator}
        notifyDanger={this.props.notifyDanger}
        isAddingToCart={this.state.loading || this.props.loading}
        addRenewableProductsToCart={this.props.addRenewableProductsToCart}
      />
    </Base>
  );

  productTypeName(__: TranslatorProps['__'], isPlural: boolean): string {
    switch (this.props.productType) {
      case 'domain':
        return isPlural ? __('Domains') : __('Domain');
      case 'simple-hosting':
        return isPlural
          ? __('Simple Hosting instances')
          : __('Simple Hosting instance');
      default:
        return isPlural ? __('Other products') : __('Other product');
    }
  }

  renderMainCell(entryCount: number, productTypeName: string): React.Node {
    return <TitleCell>{`${entryCount} ${productTypeName}`}</TitleCell>;
  }

  renderActionCell(productTypeName: string): React.Node {
    const {
      translator: { __ },
      entries,
    } = this.props;

    const allRenewablesAlreadyInCart = entries.every(entry => !!entry.product);

    return allRenewablesAlreadyInCart ? (
      <ListIcon name="checkmark" fill="success" />
    ) : (
      <Cell>
        {/* eslint-disable-next-line jsx-a11y/anchor-is-valid */}
        <a
          href="#"
          role="button"
          aria-label={__('Add %(productTypeName)s renewal to the cart', {
            productTypeName,
          })}
          disabled={this.state.loading || this.props.loading}
          onClick={this.handleAddAllToCartClick}
        >
          {__('Add')}
        </a>
      </Cell>
    );
  }

  render(): React.Node {
    const {
      translator: { __ },
      entries,
    } = this.props;

    const entryCount = entries.length;
    const productTypeName = this.productTypeName(__, entryCount !== 1);

    return (
      <Base backgroundColor="white" style={{ borderRadius: '4px 4px 0 0' }}>
        <ListRow>
          <ListItem
            backgroundColor="white"
            style={{ borderRadius: '4px 4px 0 0' }}
          >
            {this.renderMainCell(entryCount, productTypeName)}
            {this.renderActionCell(productTypeName)}
          </ListItem>
        </ListRow>
        <Base tagName="ul" padding="none" margin="none" listStyleType="none">
          {entries.map(this.renderRenewableProductRow)}
        </Base>
      </Base>
    );
  }
}
